import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { logger } from '../lib/logger.js';
import { isNativePlatform } from './device.js';

/**
 * Capacidades del dispositivo. Todas degradan sin romper nada: la app debe
 * funcionar aunque el navegador no soporte vibracion o wake lock.
 * Ninguna se da por verificada hasta probarla en dispositivo real (Fase 2).
 */

export type TipoDeVibracion = 'light' | 'medium' | 'heavy' | 'alarma';

/**
 * El patron de la alarma de fin de descanso: milisegundos alternando
 * VIBRA / PARA / VIBRA / PARA / VIBRA.
 *
 * POR QUE NO ES UN `impact`
 *
 * `Haptics.impact({ style: Heavy })` es UN pulso de 60 ms —lo dice su tabla de
 * amplitudes en el plugin: `HEAVY = { 0, 60 }`—. Eso es el golpecito que
 * confirma que has pulsado un boton, no un aviso: con el movil en el bolsillo,
 * de pie y con musica, no se nota. El fin del descanso necesita durar, y para
 * durar hace falta `Haptics.vibrate({ duration })`, que en Android es un
 * `VibrationEffect.createOneShot` a amplitud por defecto.
 *
 * Tres pulsos y no uno largo porque un motor que zumba dos segundos seguidos se
 * confunde con una llamada; el patron corto-corto-largo se reconoce.
 */
export const PATRON_ALARMA = [420, 160, 420, 160, 650] as const;

function esperar(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

/**
 * El patron, pulso a pulso, en Android.
 *
 * `Haptics.vibrate` no acepta patrones —solo una duracion— asi que la pausa la
 * pone quien llama. Cada `vibrate` vuelve en cuanto el sistema recibe la orden,
 * no cuando termina el zumbido, asi que hay que esperar la duracion entera.
 */
async function alarmaNativa(): Promise<void> {
  for (let i = 0; i < PATRON_ALARMA.length; i += 1) {
    const ms = PATRON_ALARMA[i] ?? 0;
    if (i % 2 === 0) await Haptics.vibrate({ duration: ms });
    await esperar(ms);
  }
}

export async function vibrate(kind: TipoDeVibracion = 'medium'): Promise<void> {
  try {
    if (isNativePlatform()) {
      if (kind === 'alarma') {
        await alarmaNativa();
        return;
      }
      const style =
        kind === 'light' ? ImpactStyle.Light : kind === 'heavy' ? ImpactStyle.Heavy : ImpactStyle.Medium;
      await Haptics.impact({ style });
      return;
    }
    const pattern =
      kind === 'light' ? 20 : kind === 'heavy' ? [40, 60, 40] : kind === 'alarma' ? [...PATRON_ALARMA] : 40;
    globalThis.navigator?.vibrate?.(pattern);
  } catch (error) {
    logger.debug('ui', 'HAPTICS_UNAVAILABLE', 'Vibracion no disponible', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

interface WakeLockSentinelLike {
  release(): Promise<void>;
}

let wakeLock: WakeLockSentinelLike | null = null;

/** Mantiene la pantalla encendida durante el entrenamiento (requisito §74). */
export async function keepScreenAwake(): Promise<boolean> {
  try {
    const nav = globalThis.navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> };
    };
    if (!nav.wakeLock) return false;
    wakeLock = await nav.wakeLock.request('screen');
    return true;
  } catch (error) {
    logger.debug('ui', 'WAKELOCK_UNAVAILABLE', 'Wake lock no disponible', {
      message: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

export async function releaseScreenAwake(): Promise<void> {
  try {
    await wakeLock?.release();
  } catch {
    // Sin consecuencias: el sistema libera el bloqueo al cerrar la pantalla.
  } finally {
    wakeLock = null;
  }
}

export function isOnline(): boolean {
  return globalThis.navigator?.onLine ?? true;
}
